import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { userPreferencesAPI, UserPreferences, AVAILABLE_CURRENCIES } from '@/api/userPreferences';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface UserPreferencesContextType {
  preferences: UserPreferences | null;
  isLoading: boolean;
  error: Error | null;
  updatePreferences: (newPreferences: Partial<UserPreferences>) => Promise<void>;
  availableCurrencies: string[];
}

const UserPreferencesContext = createContext<UserPreferencesContextType | undefined>(undefined);

export const UserPreferencesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const { user } = useAuth();

  // Load user preferences
  const loadPreferences = async () => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await userPreferencesAPI.getUserPreferences();
      
      if (error) {
        throw error;
      }
      
      setPreferences(data);
    } catch (err) {
      
      setError(err instanceof Error ? err : new Error('Failed to load user preferences'));
      
      toast({
        title: "Hata",
        description: "Kullanıcı tercihleri yüklenirken bir hata oluştu.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Update user preferences
  const updatePreferences = async (newPreferences: Partial<UserPreferences>) => {
    if (!user) {
      return;
    }
    
    try {
      const { data, error } = await userPreferencesAPI.updateUserPreferences(newPreferences);
      
      if (error) {
        throw error;
      }
      
      setPreferences(data);
      
      toast({
        title: "Tercihler Güncellendi",
        description: "Para birimi tercihleriniz başarıyla güncellendi.",
      });
    } catch (err) {
      
      
      toast({
        title: "Hata",
        description: "Tercihler güncellenirken bir hata oluştu.",
        variant: "destructive",
      });
      
      throw err;
    }
  };

  // Load preferences when user changes or on mount
  useEffect(() => {
    loadPreferences();
  }, [user]);

  const contextValue: UserPreferencesContextType = {
    preferences,
    isLoading,
    error,
    updatePreferences,
    availableCurrencies: AVAILABLE_CURRENCIES
  };

  return (
    <UserPreferencesContext.Provider value={contextValue}>
      {children}
    </UserPreferencesContext.Provider>
  );
};

// Hook to use the user preferences context
export const useUserPreferences = (): UserPreferencesContextType => {
  const context = useContext(UserPreferencesContext);
  
  if (context === undefined) {
    throw new Error('useUserPreferences must be used within a UserPreferencesProvider');
  }
  
  return context;
}; 